<script lang="ts">
  import IconButton from "app/components/+design-system/IconButton/IconButton.svelte";
  import Button from "app/components/+design-system/Button/Button.svelte";
  import GutterMenu from "../../+shared/GutterMenu.svelte";
  import { Clipboard, Down, Up } from "app/components/+shared/OVIcon";
  import { alerts } from "app/stores/alert";
  import { saveToClipboard } from "app/utils/clipboard";
  import { NodeViewContent, NodeViewWrapper } from "svelte-tiptap";
  import { isDarkTheme } from "app/stores/userPreferences";
  import { versionHistoryModalOpen } from "../../Collaboration/versions";
  import cx from "classnames";
  import type { NodeViewProps } from "@tiptap/core";
  import { onMount, tick } from "svelte";

  export let node: NodeViewProps["node"];
  export let updateAttributes: NodeViewProps["updateAttributes"];
  export let deleteNode: NodeViewProps["deleteNode"];
  export let isEditable: boolean;
  $$restProps; // consume unused tiptap props

  const HEIGHT_LIMIT = 2000;

  let isCollapsed = false;
  let hasOverflow = false;
  let measuredHeight: number;
  let contentHeight: number;
  let contentRef: HTMLDivElement;
  let elementRef: HTMLElement;

  let { height } = node.attrs;
  let blockHeight = height;

  $: language = node.attrs.language;

  $: allowCollapse = !(isEditable || $versionHistoryModalOpen); // only collapsable in read mode

  $: contentHeight = height || contentRef?.scrollHeight; // get height from node attribute or take measure from the client div (needed for existing blocks)
  $: hasOverflow = contentHeight > HEIGHT_LIMIT;
  $: isCollapsed = hasOverflow && allowCollapse;
  $: blockHeight = isCollapsed ? HEIGHT_LIMIT : contentHeight;

  // keep the height in sync with the content if changed while editing
  $: if (isEditable && height !== measuredHeight) {
    // wait until after paint to update to avoid competing with other updates
    tick().then(() => updateAttributes({ height: measuredHeight }));
  }

  onMount(() => {
    // add language class to code element (client side) to match nestedContent defined in CodeBlock.ts renderHTML method (server side)
    if (language) {
      const codeElement = elementRef.querySelector("code") as HTMLElement;
      if (codeElement) {
        codeElement.classList.add(`language-${language}`);
      }
    }
  });
</script>

<NodeViewWrapper class="group relative">
  {#if isEditable}
    <GutterMenu on:delete-block={deleteNode} />
  {/if}
  <!-- main block container -->
  <!-- collapse block if it is visible it and we allow it -->
  <div
    bind:this={contentRef}
    bind:clientHeight={measuredHeight}
    class={cx(
      "bg-neutral-800 text-gray-200 dark:border dark:border-white/10 dark:bg-neutral-950",
      "my-2 overflow-y-hidden rounded-lg leading-none",
      "scrollbar-thin scrollbar-stable scrollbar-rounded scrollbar-invisible group-hover:scrollbar-visible"
    )}
    style={`height: ${
      !!contentHeight && allowCollapse ? `${blockHeight}px` : "100%"
    }; transition: height 0.2s ease-in-out;`}
  >
    <!-- content wrapper with conditional gradient mask -->
    <div
      class={cx("h-full", {
        "[mask-image:_linear-gradient(to_top,_transparent_2%,_black_10%)]": isCollapsed,
      })}
      bind:this={elementRef}
    >
      <!-- code content -->
      <!-- select-text required to make code block content selectable -->
      <NodeViewContent
        as="pre"
        class="m-0 min-w-max select-text overflow-x-auto bg-neutral-800 p-2 dark:bg-neutral-950"
      />
    </div>
  </div>
  <!-- copy to clipboard -->
  <span
    class={cx("absolute right-2 top-2", {
      "backdrop-blur-sm": $isDarkTheme,
    })}
  >
    <IconButton
      icon={Clipboard}
      variant={$isDarkTheme ? "tertiary" : "theme-agnostic"}
      a11yLabel="Copy to clipboard"
      tooltipPosition="left"
      on:click={() => saveToClipboard(node.textContent, alerts.add, "Copied to clipboard")}
    />
  </span>

  <!-- expand / collapse block -->
  <span class="absolute bottom-2 right-2">
    {#if hasOverflow && allowCollapse}
      {#if isCollapsed}
        <Button
          iconRight={Down}
          label="View full block"
          variant="theme-agnostic"
          on:click={() => (isCollapsed = false)}
        />
      {:else}
        <Button
          iconRight={Up}
          label="Collapse block"
          variant="theme-agnostic"
          on:click={() => (isCollapsed = true)}
        />
      {/if}
    {/if}
  </span>
</NodeViewWrapper>
